import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  pairwise,
  startWith,
} from 'rxjs';
import {
  FilterData,
  FilterKey,
  IFilterAPI,
  LINKED_FILTER_KEYS,
} from '../standalones/components/generic-filters-view/filters.interface';
import { TelemetryService } from './telemetry.service';

@Injectable({
  providedIn: 'root',
})
export class FiltersService {
  private _filtersData = new BehaviorSubject<FilterData[]>([]);
  private _searchValue = new BehaviorSubject<string>('');
  public filtersData$ = this._filtersData.asObservable();
  public searchValue$ = this._searchValue.asObservable().pipe(distinctUntilChanged());

  constructor(private telemetry: TelemetryService) {}

  public appliedFilters$: Observable<FilterData[]> = this.filtersData$.pipe(
    map((filters: FilterData[]) => filters.filter((f: FilterData) => f.isApplied())),
  );

  // Clearing skips locked filters, so clear/reset controls should hide when nothing is clearable.
  public hasClearableFilters$: Observable<boolean> = this.appliedFilters$.pipe(
    map((filters: FilterData[]) => filters.some((f: FilterData) => !f.isLocked)),
  );

  /**
   * Emits applied filters, filtering out consecutive empty emissions (e.g. [[], []]).
   *
   * WHY: appliedFilters$ initially emits []. When initFilters() runs after a delay (e.g.,
   * loading Programs), it pushes new filters which still map to [], causing a duplicate
   * consecutive empty emission on appliedFilters$ that triggers redundant backend refetches in views.
   *
   * HOW: pairwise() discards subsequent empty arrays so views only reload on meaningful changes.
   * startWith(null) ensures the initial load emission (even if empty) is still triggered.
   */
  public appliedFiltersWithSingleEmpty$: Observable<FilterData[]> = this.appliedFilters$.pipe(
    startWith(null as any),
    pairwise(),
    filter(([prev, curr]) => prev?.length !== 0 || curr?.length !== 0),
    map(([_, curr]) => curr),
  );

  get filtersData(): FilterData[] {
    return this._filtersData.value;
  }

  get appliedFilters(): FilterData[] {
    return this.filtersData.filter((f: FilterData) => f.isApplied());
  }

  get searchValue(): string {
    return this._searchValue.value;
  }

  initFilters(filters: FilterData[]) {
    // Reset search value every time a new filter group is initialized
    this.setSearchValue('');
    this._filtersData.next(filters);
    // TODO: Start moving clearing filters logic into here
    // So that we don't have to clear filters on destroy everytime
  }

  setSearchValue(searchValue: string): void {
    this._searchValue.next(searchValue);
  }

  clearFilters(): void {
    const currentFilters = this.filtersData;
    currentFilters.forEach((f: FilterData) => {
      if (f.isLocked) {
        return;
      }

      f.handleClearFilter();
      notifyLinkedFilters(currentFilters, f);
    });
    this._filtersData.next(currentFilters);
  }

  updateFilter(filterData: FilterData): void {
    const currentFilters = this.filtersData;
    const filterToUpdate = currentFilters.find((f: FilterData) => f.key === filterData.key);

    if (filterToUpdate) {
      if (filterToUpdate.isLocked) {
        return;
      }

      Object.assign(filterToUpdate, filterData);
    } else {
      currentFilters.push(filterData);
    }
    notifyLinkedFilters(currentFilters, filterData);

    this._filtersData.next(currentFilters);
  }

  removeFilter(filterKey: FilterKey): void {
    const currentFilters = this.filtersData;
    const filterToRemove = currentFilters.find((f: FilterData) => f.key === filterKey);
    if (filterToRemove?.isLocked) {
      return;
    }

    filterToRemove?.handleClearFilter();
    notifyLinkedFilters(currentFilters, filterToRemove);

    this._filtersData.next(currentFilters);
  }

  /**
   * Generic method to apply a filter value programmatically
   * @param filterKey - The key of the filter to apply (e.g., DefaultFilterKey.SitesFilter)
   * @param selectedValue - The value(s) to apply to the filter
   * @returns boolean - true if filter was applied successfully, false otherwise
   *
   * @example
   * // Apply site filter
   * this.filtersService.applyFilterValue(DefaultFilterKey.SitesFilter, [siteObject]);
   *
   * @example
   * // Apply user filter
   * this.filtersService.applyFilterValue(DefaultFilterKey.UsersFilter, [userObject]);
   *
   * @example
   * // Apply date range filter
   * this.filtersService.applyFilterValue(DefaultFilterKey.DateRangeFilter, { start: date1, end: date2 });
   */
  applyFilterValue(filterKey: FilterKey, selectedValue: any): boolean {
    // Find the target filter
    const targetFilter = this.filtersData.find((f: FilterData) => f.key === filterKey);

    if (!targetFilter || targetFilter.isLocked) {
      return false;
    }

    // Apply the filter selection
    targetFilter.handleFilterSelection(selectedValue);
    this.updateFilter(targetFilter);
    return true;
  }

  /**
   * Logs a FullStory event when a search query is too short (i.e. 1 or 2 characters).
   * Call at the point where the search request is dispatched so that it fires regardless
   * of which UI entry point triggered the search.
   */
  logShortSearchQuery(query: string | undefined): void {
    const len = query?.trim().length ?? 0;
    if (len === 1 || len === 2) {
      this.telemetry.logBasicEvent('search-short-query', { queryLength: len });
    }
  }
}

export function notifyLinkedFilters(
  filters: FilterData[],
  sourceFilter: FilterData | undefined,
): void {
  if (!sourceFilter) {
    return;
  }

  const linkedFilterKey = LINKED_FILTER_KEYS.get(sourceFilter.key);
  if (!linkedFilterKey) {
    return;
  }

  const linkedFilter = filters.find((f) => f.key === linkedFilterKey);
  linkedFilter?.onLinkedFilterChanged(sourceFilter);
}

export function changeFiltersToAPIFormat(
  filters?: FilterData[],
  searchValue?: string,
): IFilterAPI | undefined {
  const filterAPI =
    filters
      ?.filter((f: FilterData) => f.isApplied())
      .reduce((apiFilters, f: FilterData) => {
        f.convertToAPIFormat(apiFilters);
        return apiFilters;
      }, {} as IFilterAPI) || {};

  const search = searchValue?.trim();
  return search ? { ...filterAPI, search } : filterAPI;
}

export function encodeFiltersURI(filters: IFilterAPI): string {
  return btoa(encodeURIComponent(JSON.stringify(filters)));
}
